import type { AppEvent } from "./api";

export interface DesktopSettings {
  octoApiToken: string;
  octoLocalApi: string;
  /** Upstream pull API URL (synced to server poller) */
  apiLocation: string;
  /** Upstream / desktop access token */
  apiAccessToken: string;
  /** socks5://user:pass@host:port — Electron → hosted server */
  socks5Proxy: string;
  /** Persisted hosted server domain/origin */
  serverDomain?: string;
  /** Admin-only: auto-open Octo when enrichment finishes */
  stationMode?: boolean;
  /** When true, Station does not auto-open Octo */
  pauseStash?: boolean;
  /** When true, finished admin logins go to Stash → Pending. */
  stashEnabled?: boolean;
  /** Collector origin. Remote hosts use SOCKS5; localhost stays direct. */
  collectorApiUrl?: string;
}

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  lastSyncAt: string | null;
  lastEventId: number;
  outboxPending: number;
  error: string | null;
}

export interface BrexDesktopApi {
  isDesktop: true;
  info: () => Promise<{
    isDesktop: boolean;
    dbPath: string;
    eventCount: number;
    octoLocalApi: string;
    hasOctoToken?: boolean;
    hasApiToken?: boolean;
    hasSocks5?: boolean;
    serverOrigin?: string | null;
    serverDomain?: string;
    localUiOrigin?: string | null;
  }>;
  getConnectConfig?: () => Promise<{ serverDomain: string; socks5Proxy: string }>;
  connectToServer?: (
    input: { serverDomain: string; socks5Proxy: string } | string
  ) => Promise<{ ok: true; url: string } | { ok: false; error: string }>;
  getLastServerUrl?: () => Promise<string>;
  getSettings: () => Promise<DesktopSettings>;
  saveSettings: (
    partial: Partial<DesktopSettings>
  ) => Promise<{ ok: true; settings: DesktopSettings } | { ok: false; error: string }>;
  http?: (req: {
    path: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string | null;
  }) => Promise<{
    ok: boolean;
    status: number;
    headers?: Record<string, string>;
    body: string;
    data: unknown;
  }>;
  collectorHttp?: (req: {
    path: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string | null;
  }) => Promise<{
    ok: boolean;
    status: number;
    headers?: Record<string, string>;
    body: string;
    data: unknown;
  }>;
  requestAttention?: () => Promise<{ ok: boolean }>;
  listEvents?: (opts?: {
    limit?: number;
    afterId?: number;
    stockStage?: string | string[];
    includeHidden?: boolean;
    onlyHidden?: boolean;
    status?: string;
  }) => Promise<{ events: AppEvent[] }>;
  getEvent?: (id: number) => Promise<{ event: AppEvent | null }>;
  applyServerEvent?: (event: AppEvent) => Promise<{ ok: boolean; event?: AppEvent }>;
  syncEvent: (event: AppEvent) => Promise<{ ok: boolean; eventCount?: number }>;
  enqueueMutation?: (input: {
    kind: "stock" | "notes" | "clear_operator_alert" | "list_used";
    eventId: number;
    stage?: string;
    channel?: string;
    notes?: string;
    used?: boolean;
  }) => Promise<{ ok: boolean; event?: AppEvent; error?: string }>;
  getSyncStatus?: () => Promise<SyncStatus>;
  startSync?: () => Promise<{ ok: boolean; error?: string }>;
  reconnectSync?: () => Promise<{ ok: boolean; error?: string }>;
  stopSync?: () => Promise<{ ok: boolean; error?: string }>;
  onDbChanged?: (cb: (info: { reason: string }) => void) => () => void;
  onSyncStatus?: (cb: (status: SyncStatus) => void) => () => void;
  onAssignment?: (
    cb: (msg: {
      type: "assignment" | "unassignment";
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events?: AppEvent[];
      eventId?: number;
    }) => void
  ) => () => void;
  openInOcto: (input: {
    title?: string;
    cookies?: string;
    proxy?: unknown;
    startUrl?: string;
    hasToken?: boolean;
    token?: string;
    /** Always true for manual opens; Station uses ~50%. */
    enablePasskey?: boolean;
    /** Station-only: auto Create passkey then close browser. */
    enrollPasskey?: boolean;
    passkeyId?: string;
    brexTokens?: {
      accessToken?: string;
      idToken?: string;
      lastAuth?: string;
      state?: string;
      id?: string;
      expiresIn?: string | number;
      requestInitiationTime?: string | number;
    };
  }) => Promise<{ ok: true; data: unknown } | { ok: false; error: string }>;
}

declare global {
  interface Window {
    brexDesktop?: BrexDesktopApi;
  }
}

export function isElectronDesktop(): boolean {
  return Boolean(window.brexDesktop?.isDesktop);
}

export async function syncEventToDesktop(event: AppEvent): Promise<void> {
  if (!window.brexDesktop) return;
  try {
    await window.brexDesktop.syncEvent(event);
  } catch {
    // local mirror is best-effort
  }
}

let cachedDesktopSettings: DesktopSettings | null = null;

export async function refreshDesktopSettingsCache(): Promise<DesktopSettings | null> {
  if (!window.brexDesktop) {
    cachedDesktopSettings = null;
    return null;
  }
  try {
    cachedDesktopSettings = await window.brexDesktop.getSettings();
    return cachedDesktopSettings;
  } catch {
    return cachedDesktopSettings;
  }
}

export function getDesktopSettingsCache(): DesktopSettings | null {
  return cachedDesktopSettings;
}

/** Optimistic stock/notes via local DB + outbox when desktop; otherwise REST. */
export async function mutateStock(
  eventId: number,
  body: { stage: string; channel?: string; claim?: boolean }
): Promise<AppEvent> {
  // Atomic claim must hit the server hub
  if (body.claim || !window.brexDesktop?.enqueueMutation) {
    const { api } = await import("./api");
    const data = await api<{ event: AppEvent }>(`/api/admin/events/${eventId}/stock`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (window.brexDesktop?.applyServerEvent && data.event) {
      void window.brexDesktop.applyServerEvent(data.event);
    }
    return data.event;
  }
  const result = await window.brexDesktop.enqueueMutation({
    kind: "stock",
    eventId,
    stage: body.stage,
    channel: body.channel,
  });
  if (!result.ok || !result.event) {
    throw new Error(result.error || "Local stock mutation failed");
  }
  return result.event;
}

export async function mutateNotes(eventId: number, notes: string): Promise<AppEvent> {
  if (!window.brexDesktop?.enqueueMutation) {
    const { api } = await import("./api");
    const data = await api<{ event: AppEvent }>(`/api/admin/events/${eventId}/notes`, {
      method: "PATCH",
      body: JSON.stringify({ notes }),
    });
    return data.event;
  }
  const result = await window.brexDesktop.enqueueMutation({
    kind: "notes",
    eventId,
    notes,
  });
  if (!result.ok || !result.event) {
    throw new Error(result.error || "Local notes mutation failed");
  }
  return result.event;
}

export async function mutateListUsed(eventId: number, used: boolean): Promise<AppEvent> {
  if (!window.brexDesktop?.enqueueMutation) {
    const { api } = await import("./api");
    const data = await api<{ event: AppEvent }>(`/api/admin/events/${eventId}/list-used`, {
      method: "POST",
      body: JSON.stringify({ used }),
    });
    if (window.brexDesktop?.applyServerEvent && data.event) {
      void window.brexDesktop.applyServerEvent(data.event);
    }
    return data.event;
  }
  const result = await window.brexDesktop.enqueueMutation({
    kind: "list_used",
    eventId,
    used,
  });
  if (!result.ok || !result.event) {
    throw new Error(result.error || "Failed to update used flag");
  }
  return result.event;
}

export async function mutatePhoneAdd(
  eventId: number,
  action: "yes" | "no",
  sessionId?: string
): Promise<AppEvent> {
  const { api } = await import("./api");
  const data = await api<{ event: AppEvent }>(`/api/admin/events/${eventId}/phone-add`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
  if (window.brexDesktop?.applyServerEvent && data.event) {
    void window.brexDesktop.applyServerEvent(data.event);
  }
  const sid = String(sessionId || "").trim();
  if (sid && window.brexDesktop?.collectorHttp) {
    void window.brexDesktop.collectorHttp({
      path: "/api/operator-action",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: sid, action }),
    });
  }
  return data.event;
}

export async function mutateClearOperatorAlert(
  eventId: number,
  sessionId?: string
): Promise<AppEvent> {
  const sid = String(sessionId || "").trim();
  if (sid && window.brexDesktop?.collectorHttp) {
    void window.brexDesktop.collectorHttp({
      path: "/api/operator-action",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: sid, action: "keep" }),
    });
  }
  if (!window.brexDesktop?.enqueueMutation) {
    const { api } = await import("./api");
    const data = await api<{ event: AppEvent }>(
      `/api/admin/events/${eventId}/clear-operator-alert`,
      { method: "POST" }
    );
    return data.event;
  }
  const result = await window.brexDesktop.enqueueMutation({
    kind: "clear_operator_alert",
    eventId,
  });
  if (!result.ok || !result.event) {
    throw new Error(result.error || "Failed to clear operator alert");
  }
  return result.event;
}
